import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, ArrowLeft, FileText, MessageSquare, AlertTriangle } from 'lucide-react'
import api from '../../services/api'
import PlatformBanner from './PlatformBanner.jsx'
import { themeFor, themeVars, productLabel } from './dashboardTheme.js'
import { useTranslation } from 'react-i18next'

/**
 * ProductRooms — every country room this person may open, inside one product.
 *
 * A room is listed whether or not anything has been started in it. Someone whose country has no
 * files yet still needs somewhere to put the first one, and an empty list would read as "you
 * have no access" when the truth is "nobody has started yet".
 */
/**
 * A country's flag, drawn from its two-letter code.
 *
 * Deliberately NOT a flag emoji: Windows ships no glyphs for them, so a third of the team would
 * see the letters "KE" where everyone else sees a flag. A small image works everywhere.
 */
function Flag({ alpha2, name }) {
  const [failed, setFailed] = useState(false)
  if (!alpha2 || failed) return null
  const code = alpha2.toLowerCase()
  return (
    <img
      src={`https://flagcdn.com/w80/${code}.png`}
      srcSet={`https://flagcdn.com/w160/${code}.png 2x`}
      loading="lazy" alt=""
      aria-hidden="true"
      title={name}
      onError={() => setFailed(true)}
      // A fixed box so a hundred and eighteen flags line up down the page whatever their true
      // proportions — several are not 3:2, and ragged edges would read as a rendering fault.
      className="shrink-0 w-14 h-10 rounded-md object-cover shadow-sm ring-1 ring-black/10"
    />
  )
}

export default function ProductRooms() {
  const { t } = useTranslation('platform')
  const { productKey } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [q, setQ] = useState('')

  useEffect(() => {
    let alive = true
    api.get(`/platform/products/${productKey}/rooms`)
      .then(r => { if (alive) setData(r.data) })
      .catch(e => { if (alive) setError(e.response?.data?.detail || 'Could not load your rooms.') })
    return () => { alive = false }
  }, [productKey])

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => navigate('/products')} className="text-xs text-gray-500 hover:text-[var(--pt-accent-dark,#4338CA)] inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> My Products
        </button>
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      </div>
    )
  }
  if (!data) {
    return <div className="p-6 flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading your rooms…</div>
  }

  const label = productLabel(productKey, data.product?.name)
  const needle = q.trim().toLowerCase()
  // Searchable by code as well as name — someone who works in ISO codes all day should be able
  // to type KEN.
  const rooms = (data.rooms || []).filter(r => !needle
    || (r.country || '').toLowerCase().includes(needle)
    || (r.country_iso || '').toLowerCase().includes(needle)
    || (r.region || '').toLowerCase().includes(needle))

  // The rooms somebody is actually working in, first — and within those, the ones waiting on
  // this person before the ones merely recent. A hundred and eighteen countries in alphabetical
  // order buries the two that matter today, and a comment left five minutes ago is not more
  // important than a document sitting there waiting to be sent.
  const working = rooms.filter(r => r.started && (r.last_activity || r.drafts || r.submitted || r.delivered))
  working.sort((a, b) => {
    if (!!b.needs_me !== !!a.needs_me) return b.needs_me ? 1 : -1
    return new Date(b.last_activity || 0) - new Date(a.last_activity || 0)
  })

  // Everything else, by region. Finding one particular country is what the search box is for,
  // so the working rooms are not repeated down here as well.
  const untouched = rooms.filter(r => !working.includes(r))
  const byRegion = {}
  for (const r of untouched) (byRegion[r.region || 'Other'] ||= []).push(r)

  const when = (iso) => {
    if (!iso) return null
    const mins = Math.round((Date.now() - new Date(iso)) / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins} min ago`
    const h = Math.round(mins / 60)
    if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`
    const d = Math.round(h / 24)
    return d < 30 ? `${d} day${d === 1 ? '' : 's'} ago` : new Date(iso).toLocaleDateString()
  }

  const Card = ({ r }) => (
    <button key={r.country_iso}
            onClick={() => navigate(`/products/${productKey}/rooms/${r.country_iso}`)}
            className={`text-left bg-white rounded-xl border p-3.5 transition hover:shadow-sm
              ${r.needs_me ? 'border-amber-300 hover:border-amber-400' : 'border-gray-200 hover:border-[var(--pt-accent,#818CF8)]'}`}>
      {/* The flag sits beside the WHOLE card, centred against it — not tucked into the top
          corner. Scanning a hundred and eighteen countries by name is slow; a flag is
          recognised before the word is read, and it only does that job at a size worth
          looking at. */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 text-sm truncate">{r.country}</p>
          <p className="text-[10px] text-gray-400 tracking-wide">{r.country_iso}</p>

          {r.needs_me && (
            <span className="inline-block mt-1.5 text-[9px] font-bold uppercase tracking-wide text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5">
              needs you
            </span>
          )}

          {r.started ? (
            <>
              <p className="text-[11px] text-gray-500 mt-1.5 flex flex-wrap gap-x-3">
                {r.ready_to_submit > 0 && <span className="text-amber-700 font-medium">{r.ready_to_submit} ready to submit</span>}
                {r.drafts > 0 && <span>{r.drafts} in progress</span>}
                {r.submitted > 0 && <span className="text-amber-700">{r.submitted} awaiting hand-over</span>}
                {r.delivered > 0 && <span className="text-green-700">{r.delivered} delivered</span>}
                {!r.drafts && !r.submitted && !r.delivered && <span>{t('open')}</span>}
              </p>
              {r.last_activity && (
                <p className="text-[10px] text-gray-400 mt-1">last worked on {when(r.last_activity)}</p>
              )}
            </>
          ) : (
            <p className="text-[11px] text-gray-400 mt-1.5">{t('nothing_started')}</p>
          )}
        </div>

        <Flag alpha2={r.alpha2} name={r.country} />
      </div>
    </button>
  )

  return (
    <div className="p-6 max-w-5xl mx-auto" style={themeVars(themeFor(productKey))}>
      <PlatformBanner theme={themeFor(productKey)} title={t('document_rooms')}
                      back={{ label, onClick: () => navigate(`/products/${productKey}`) }}>
        One room per country. Upload a file, talk it through, and your admin submits it when the
        room agrees. Your role here: <span className="font-semibold text-white">
          {data.my_role || 'none of your own — ProTrack Super Admin oversight'}</span>
        {data.may_submit && <> — you can submit.</>}
      </PlatformBanner>

      {data.rooms?.length > 6 && (
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('ph_find_country')}
               className="mt-4 w-full sm:w-80 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      )}

      {data.rooms?.length === 0 ? (
        <p className="mt-6 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            You don&rsquo;t have a country or region on your account yet, so there are no rooms to
            show. That comes from {data.product?.name || 'the product'} when you sign in — ask
            their administrator to check it.
          </span>
        </p>
      ) : (
        <>
          {working.length > 0 && (
            <div className="mt-6">
              <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
                Being worked on
                {working.some(r => r.needs_me) && (
                  <span className="ml-2 normal-case tracking-normal text-amber-700 font-medium">
                    · {working.filter(r => r.needs_me).length} waiting on you
                  </span>
                )}
              </p>
              <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                {working.map(r => <Card key={r.country_iso} r={r} />)}
              </div>
            </div>
          )}

          {Object.entries(byRegion).map(([region, list]) => (
            <div key={region} className="mt-6">
              {(Object.keys(byRegion).length > 1 || working.length > 0) && (
                <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">{region}</p>
              )}
              <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                {list.map(r => <Card key={r.country_iso} r={r} />)}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  )
}
